import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import type {
  CalculationAssumptionsView,
  MediaItemKind,
  MediaItemRecord,
  TenancyRecord,
  UnitCostItemRecord,
  UnitLinkRecord,
} from '@brimo/shared';
import { ApiError, GENERIC_ERROR_MESSAGE, api } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import type { DocumentRow } from '../contacts/types';
import type {
  AllUnitsFilter,
  AllUnitsResponse,
  ArchivePreview,
  Calculation,
  CalculationsResponse,
  CalculatorContext,
  ChecklistResponse,
  ContactLinkedDeal,
  ContactSearchItem,
  CostItemBody,
  CreateCalculationBody,
  CreateDeveloperBody,
  CreateInvestmentCalculationBody,
  CreatePropertyBody,
  DealAppointmentItem,
  Developer,
  DeveloperLinkResponse,
  DeveloperLinkSendResponse,
  DocumentType,
  PatchMediaBody,
  PatchPropertyBody,
  PatchUnitBody,
  PropertyListResponse,
  PropertyRecord,
  PublicUploadPage,
  PublicUploadResponse,
  ReleaseRequestResponse,
  ReleaseResponse,
  ReservationItemLite,
  TenancyBody,
  UnitDetail,
  UnitFilter,
  UnitSort,
  UnitsResponse,
  UploadResultItem,
  WizardDocument,
  WizardUploadRow,
} from './types';
import { uploadWithProgress, type UploadOptions } from './upload';

/** Query keys of the module under the roots of `lib/events.ts` (`property.updated`, `unit.updated`, `document.updated`). */
export const propertyKeys = {
  list: (filter: PropertyListFilter) => [...queryRoots.properties, 'list', filter] as const,
  record: (id: string) => [...queryRoots.property, id] as const,
  wizard: (id: string) => [...queryRoots.property, id, 'wizard'] as const,
  calculations: (id: string) => [...queryRoots.property, id, 'calculations'] as const,
  archivePreview: (id: string) => [...queryRoots.property, id, 'archive-preview'] as const,
  units: (propertyId: string, filter: UnitFilter) => [...queryRoots.units, 'of', propertyId, filter] as const,
  unit: (id: string) => [...queryRoots.units, 'detail', id] as const,
  unitCalculations: (id: string) => [...queryRoots.units, 'detail', id, 'calculations'] as const,
  developers: (q: string) => [...queryRoots.properties, 'developers', q] as const,
  documentTypes: () => [...queryRoots.documents, 'types'] as const,
  calculatorContext: (params: Record<string, string>) => [...queryRoots.units, 'calculator', params] as const,
  contactSearch: (q: string) => [...queryRoots.contacts, 'calculator-search', q] as const,
  contactDeals: (contactId: string) => [...queryRoots.contact, contactId, 'calculator-deals'] as const,
  publicPage: (token: string) => ['public-upload', token] as const,
};

export interface PropertyListFilter {
  q?: string | undefined;
  status?: string[] | undefined;
}

function query(params: Record<string, string | string[] | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      for (const entry of value) search.append(key, entry);
    } else search.set(key, value);
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

// --- list and record ----------------------------------------------------------------------------------

export const LIST_PAGE_SIZE = 24;

export function useProperties(filter: PropertyListFilter) {
  const clean: PropertyListFilter = {};
  if (filter.q) clean.q = filter.q;
  if (filter.status && filter.status.length > 0) clean.status = [...filter.status].sort();
  return useInfiniteQuery({
    queryKey: propertyKeys.list(clean),
    queryFn: ({ pageParam, signal }) =>
      api<PropertyListResponse>(
        `/properties${query({ q: clean.q, status: clean.status, cursor: pageParam ?? undefined, limit: String(LIST_PAGE_SIZE) })}`,
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function usePropertyRecord(id: string, enabled = true): UseQueryResult<PropertyRecord, Error> {
  return useQuery({
    queryKey: propertyKeys.record(id),
    queryFn: ({ signal }) => api<PropertyRecord>(`/properties/${id}`, { signal }),
    enabled: enabled && id !== '',
    retry: (count, error) =>
      !(error instanceof ApiError && (error.status === 404 || error.status === 403)) && count < 1,
    staleTime: 15_000,
  });
}

export function updateProperty(id: string, body: PatchPropertyBody): Promise<PropertyRecord> {
  return api<PropertyRecord>(`/properties/${id}`, { method: 'PATCH', body });
}

/**
 * The organization's assumptions in force, for the hints „Organisation: 4,7 %" of the „Objekt-Vorgaben" (D-361).
 * Same query key as the settings page; only fetched for users who may read them (`settings.organization` yes/team).
 */
export function useOrganizationAssumptions(enabled: boolean) {
  return useQuery({
    queryKey: [...queryRoots.organization, 'calculation-assumptions'],
    queryFn: ({ signal }) =>
      api<CalculationAssumptionsView>('/settings/organization/calculation-assumptions', { signal }),
    enabled,
    staleTime: 60_000,
  });
}

/** „Speichern und bestätigen" of the base-data sheet: 409 BASE_DATA_INCOMPLETE while required data is missing. */
export function confirmBaseData(id: string): Promise<PropertyRecord> {
  return api<PropertyRecord>(`/properties/${id}/base-data/confirm`, { method: 'POST', body: {} });
}

export function createProperty(body: CreatePropertyBody): Promise<PropertyRecord> {
  return api<PropertyRecord>('/properties', { method: 'POST', body });
}

export function useArchivePreview(id: string, enabled: boolean) {
  return useQuery({
    queryKey: propertyKeys.archivePreview(id),
    queryFn: ({ signal }) => api<ArchivePreview>(`/properties/${id}/archive-preview`, { signal }),
    enabled,
    staleTime: 0,
  });
}

export function archiveProperty(id: string, reason: string): Promise<PropertyRecord> {
  return api<PropertyRecord>(`/properties/${id}/archive`, { method: 'POST', body: { reason } });
}

export function restoreProperty(id: string): Promise<PropertyRecord> {
  return api<PropertyRecord>(`/properties/${id}/restore`, { method: 'POST', body: {} });
}

/** Refreshes everything of one property (record, wizard, units) after a mutation. */
export function useInvalidateProperty() {
  const queryClient = useQueryClient();
  return useCallback(
    async (id: string) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...queryRoots.property, id] }),
        queryClient.invalidateQueries({ queryKey: [...queryRoots.properties] }),
        queryClient.invalidateQueries({ queryKey: [...queryRoots.units] }),
      ]);
    },
    [queryClient],
  );
}

// --- units --------------------------------------------------------------------------------------------

export const UNITS_PAGE_SIZE = 10;

/** Query key of one units-table listing (filter, reservable switch and column sort of Objekte 03). */
export function unitsQueryKey(propertyId: string, filter: UnitFilter, reservable: boolean, sort: UnitSort) {
  return [
    ...propertyKeys.units(propertyId, filter),
    reservable ? 'reservable' : 'any',
    `${sort.key}-${sort.direction}`,
  ] as const;
}

const DEFAULT_UNIT_SORT: UnitSort = { key: 'number', direction: 'asc' };

/** Units table pages; `reservable` keeps only units that can be reserved now (server filter); `sort` D-366. */
export function useUnits(
  propertyId: string,
  filter: UnitFilter,
  enabled = true,
  reservable = false,
  sort: UnitSort = DEFAULT_UNIT_SORT,
) {
  return useInfiniteQuery({
    queryKey: unitsQueryKey(propertyId, filter, reservable, sort),
    queryFn: ({ pageParam, signal }) =>
      api<UnitsResponse>(
        `/properties/${propertyId}/units${query({ availability: filter, reservable: reservable ? 'true' : undefined, sort: sort.key, direction: sort.direction, cursor: pageParam ?? undefined, limit: String(UNITS_PAGE_SIZE) })}`,
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    enabled: enabled && propertyId !== '',
    staleTime: 15_000,
  });
}

export const ALL_UNITS_PAGE_SIZE = 24;

/** „Alle Einheiten" (`GET /units`): every visible unit across the properties, paged with the keyset cursor. */
export function useAllUnits(filter: AllUnitsFilter) {
  const text = (value: number | undefined) => (value === undefined ? undefined : String(value));
  const params = {
    q: filter.q,
    propertyId: filter.propertyId,
    availability: filter.availability === 'all' ? undefined : filter.availability,
    priceMinCents: text(filter.priceMinCents),
    priceMaxCents: text(filter.priceMaxCents),
    areaMinSqm: text(filter.areaMinSqm),
    areaMaxSqm: text(filter.areaMaxSqm),
    budgetCents: text(filter.budgetCents),
    contactId: filter.contactId,
    sort: filter.sort === 'property' ? undefined : filter.sort,
  };
  return useInfiniteQuery({
    queryKey: [...queryRoots.units, 'all', params] as const,
    queryFn: ({ pageParam, signal }) =>
      api<AllUnitsResponse>(
        `/units${query({ ...params, cursor: pageParam ?? undefined, limit: String(ALL_UNITS_PAGE_SIZE) })}`,
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

/** All units of a property in one page (pickers: „Einheit verknüpfen"); the route allows up to 200. */
export function usePropertyUnitOptions(propertyId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryRoots.units, 'options', propertyId] as const,
    queryFn: ({ signal }) =>
      api<UnitsResponse>(`/properties/${propertyId}/units${query({ limit: '200' })}`, { signal }),
    enabled: enabled && propertyId !== '',
    staleTime: 30_000,
  });
}

export function useUnit(id: string): UseQueryResult<UnitDetail, Error> {
  return useQuery({
    queryKey: propertyKeys.unit(id),
    queryFn: ({ signal }) => api<UnitDetail>(`/units/${id}`, { signal }),
    enabled: id !== '',
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 1,
    staleTime: 15_000,
  });
}

export function updateUnit(id: string, body: PatchUnitBody): Promise<UnitDetail> {
  return api<UnitDetail>(`/units/${id}`, { method: 'PATCH', body });
}

export function remindReservation(reservationId: string): Promise<unknown> {
  return api<unknown>(`/reservations/${reservationId}/remind`, { method: 'POST', body: {} });
}

/**
 * `GET` that answers with a file (`/properties/:id/price-list.csv`): saved through a temporary object
 * URL under the server's file name. Errors arrive as the usual JSON envelope.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<string> {
  const response = await fetch(`/api${path}`, {
    headers: { Accept: 'text/csv, application/pdf, application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) {
    const text = await response.text();
    let body: { error?: { code?: string; message?: string } } | null = null;
    try {
      body = text ? (JSON.parse(text) as { error?: { code?: string; message?: string } }) : null;
    } catch {
      body = null;
    }
    throw new ApiError(response.status, {
      code: (body?.error?.code ?? 'INTERNAL') as ApiError['code'],
      message: body?.error?.message ?? GENERIC_ERROR_MESSAGE,
    });
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const encoded = disposition ? /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1] : undefined;
  let name = fallbackName;
  if (encoded) {
    try {
      name = decodeURIComponent(encoded);
    } catch {
      name = fallbackName;
    }
  } else if (disposition) {
    name = /filename="([^"]+)"/i.exec(disposition)?.[1] ?? fallbackName;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return name;
}

// --- media, cost items, links, tenancies (docs/dev/api-properties.md „Data model extension") -----------

export type MediaOwner = { propertyId: string } | { unitId: string };

function mediaBase(owner: MediaOwner): string {
  return 'propertyId' in owner ? `/properties/${owner.propertyId}/media` : `/units/${owner.unitId}/media`;
}

/** `POST /properties/:id/media` or `/units/:id/media`: one JPEG/PNG per request so that each file reports progress. */
export function uploadMedia(
  owner: MediaOwner,
  file: File,
  kind: MediaItemKind,
  options: UploadOptions = {},
): Promise<MediaItemRecord> {
  const form = new FormData();
  form.set('kind', kind);
  form.set('file', file, file.name);
  return uploadWithProgress<MediaItemRecord>(mediaBase(owner), form, options);
}

export function updateMedia(id: string, body: PatchMediaBody): Promise<MediaItemRecord> {
  return api<MediaItemRecord>(`/media/${id}`, { method: 'PATCH', body });
}

export function orderMedia(owner: MediaOwner, ids: string[]): Promise<{ items: MediaItemRecord[] }> {
  return api<{ items: MediaItemRecord[] }>(`${mediaBase(owner)}/order`, { method: 'PUT', body: { ids } });
}

export function deleteMedia(id: string): Promise<void> {
  return api<void>(`/media/${id}`, { method: 'DELETE' });
}

export function createCostItem(unitId: string, body: CostItemBody): Promise<UnitCostItemRecord> {
  return api<UnitCostItemRecord>(`/units/${unitId}/cost-items`, { method: 'POST', body });
}

export function updateCostItem(
  unitId: string,
  itemId: string,
  body: Partial<CostItemBody>,
): Promise<UnitCostItemRecord> {
  return api<UnitCostItemRecord>(`/units/${unitId}/cost-items/${itemId}`, { method: 'PATCH', body });
}

export function deleteCostItem(unitId: string, itemId: string): Promise<void> {
  return api<void>(`/units/${unitId}/cost-items/${itemId}`, { method: 'DELETE' });
}

export function orderCostItems(unitId: string, ids: string[]): Promise<{ items: UnitCostItemRecord[] }> {
  return api<{ items: UnitCostItemRecord[] }>(`/units/${unitId}/cost-items/order`, {
    method: 'PUT',
    body: { ids },
  });
}

export function linkUnit(unitId: string, relatedUnitId: string): Promise<{ items: UnitLinkRecord[] }> {
  return api<{ items: UnitLinkRecord[] }>(`/units/${unitId}/links`, {
    method: 'POST',
    body: { relatedUnitId },
  });
}

export function unlinkUnit(unitId: string, relatedUnitId: string): Promise<void> {
  return api<void>(`/units/${unitId}/links/${relatedUnitId}`, { method: 'DELETE' });
}

export function createTenancy(unitId: string, body: TenancyBody): Promise<TenancyRecord> {
  return api<TenancyRecord>(`/units/${unitId}/tenancies`, { method: 'POST', body });
}

export function updateTenancy(
  unitId: string,
  tenancyId: string,
  body: Partial<TenancyBody>,
): Promise<TenancyRecord> {
  return api<TenancyRecord>(`/units/${unitId}/tenancies/${tenancyId}`, { method: 'PATCH', body });
}

export function deleteTenancy(unitId: string, tenancyId: string): Promise<void> {
  return api<void>(`/units/${unitId}/tenancies/${tenancyId}`, { method: 'DELETE' });
}

// --- documents ----------------------------------------------------------------------------------------

export function useDocumentTypes(enabled = true) {
  return useQuery({
    queryKey: propertyKeys.documentTypes(),
    queryFn: ({ signal }) => api<{ items: DocumentType[] }>('/document-types', { signal }),
    enabled,
    staleTime: 300_000,
  });
}

/** The record a document is uploaded to: property, unit or (0032) one of the unit's tenancies. */
export type DocumentOwnerRef = { propertyId: string } | { unitId: string } | { tenancyId: string };

export interface OwnerDocumentUpload {
  owner: DocumentOwnerRef;
  party: 'broker' | 'developer' | 'internal';
  documentTypeId?: string | undefined;
  name?: string | undefined;
  file: File;
}

/** `POST /documents` with a property or unit as owner (Objekte 03 „Unterlagen hochladen", 04 „Unterlage hochladen"). */
export function uploadOwnerDocument(
  input: OwnerDocumentUpload,
  options: UploadOptions = {},
): Promise<DocumentRow> {
  const form = new FormData();
  if ('propertyId' in input.owner) form.set('propertyId', input.owner.propertyId);
  else if ('unitId' in input.owner) form.set('unitId', input.owner.unitId);
  else form.set('tenancyId', input.owner.tenancyId);
  form.set('party', input.party);
  if (input.documentTypeId) form.set('documentTypeId', input.documentTypeId);
  if (input.name) form.set('name', input.name);
  form.set('file', input.file, input.file.name);
  return uploadWithProgress<DocumentRow>('/documents', form, options);
}

// --- developers ---------------------------------------------------------------------------------------

export function useDevelopers(q: string, enabled = true) {
  return useQuery({
    queryKey: propertyKeys.developers(q),
    queryFn: ({ signal }) =>
      api<{ items: Developer[] }>(`/developers${query({ q, limit: '20' })}`, { signal }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function createDeveloper(body: CreateDeveloperBody): Promise<Developer> {
  return api<Developer>('/developers', { method: 'POST', body });
}

// --- wizard -------------------------------------------------------------------------------------------

export function useWizard(id: string, enabled = true): UseQueryResult<WizardDocument, Error> {
  return useQuery({
    queryKey: propertyKeys.wizard(id),
    queryFn: ({ signal }) => api<WizardDocument>(`/properties/${id}/wizard`, { signal }),
    enabled: enabled && id !== '',
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 1,
    staleTime: 10_000,
  });
}

/** One multipart request per file so that every row gets its own progress (the route accepts up to 20 parts). */
export function uploadWizardFile(
  propertyId: string,
  file: File,
  options: UploadOptions = {},
): Promise<{ items: UploadResultItem[] }> {
  const form = new FormData();
  form.set('file', file, file.name);
  return uploadWithProgress<{ items: UploadResultItem[] }>(
    `/properties/${propertyId}/documents`,
    form,
    options,
  );
}

/** „Alle entfernen" (Objekt anlegen 02): documents are archived, never deleted (api-documents.md). */
export function archiveDocument(documentId: string): Promise<unknown> {
  return api<unknown>(`/documents/${documentId}`, { method: 'DELETE' });
}

export function assignDocument(documentId: string, documentTypeId: string | null): Promise<WizardUploadRow> {
  return api<WizardUploadRow>(`/documents/${documentId}/assign`, {
    method: 'POST',
    body: { documentTypeId },
  });
}

/** `GET /properties/:id/calculations` — the property record's „Ankaufskalkulation" card (D-354). */
export function usePropertyCalculations(
  id: string,
  enabled: boolean,
): UseQueryResult<CalculationsResponse, Error> {
  return useQuery({
    queryKey: propertyKeys.calculations(id),
    queryFn: ({ signal }) => api<CalculationsResponse>(`/properties/${id}/calculations`, { signal }),
    enabled: enabled && id !== '',
    staleTime: 15_000,
  });
}

export function saveCalculation(
  propertyId: string,
  body: CreateCalculationBody,
): Promise<CalculationsResponse> {
  return api<CalculationsResponse>(`/properties/${propertyId}/calculations`, { method: 'POST', body });
}

export function setChecklistItem(
  propertyId: string,
  itemId: string,
  done: boolean,
): Promise<ChecklistResponse> {
  return api<ChecklistResponse>(`/properties/${propertyId}/checklist/${itemId}`, {
    method: 'POST',
    body: { done },
  });
}

export function requestChecklistItem(propertyId: string, itemId: string): Promise<ChecklistResponse> {
  return api<ChecklistResponse>(`/properties/${propertyId}/checklist/${itemId}/request`, {
    method: 'POST',
    body: {},
  });
}

export function remindChecklistItem(propertyId: string, itemId: string): Promise<ChecklistResponse> {
  return api<ChecklistResponse>(`/properties/${propertyId}/checklist/${itemId}/remind`, {
    method: 'POST',
    body: {},
  });
}

export function releaseProperty(propertyId: string): Promise<ReleaseResponse> {
  return api<ReleaseResponse>(`/properties/${propertyId}/release`, { method: 'POST', body: {} });
}

export function requestRelease(
  propertyId: string,
  body: { recipientId: string; message?: string | null },
): Promise<ReleaseRequestResponse> {
  return api<ReleaseRequestResponse>(`/properties/${propertyId}/release-request`, { method: 'POST', body });
}

export function withdrawProperty(propertyId: string, reason: string): Promise<void> {
  return api<void>(`/properties/${propertyId}/withdraw`, { method: 'POST', body: { reason } });
}

export function createDeveloperLink(propertyId: string): Promise<DeveloperLinkResponse> {
  return api<DeveloperLinkResponse>(`/properties/${propertyId}/developer-link`, { method: 'POST', body: {} });
}

export function sendDeveloperLink(propertyId: string): Promise<DeveloperLinkSendResponse> {
  return api<DeveloperLinkSendResponse>(`/properties/${propertyId}/developer-link/send`, {
    method: 'POST',
    body: {},
  });
}

// --- investment calculator ----------------------------------------------------------------------------

export function useCalculatorContext(params: { unitId?: string; contactId?: string; dealId?: string }) {
  const clean: Record<string, string> = {};
  if (params.unitId) clean['unitId'] = params.unitId;
  if (params.contactId) clean['contactId'] = params.contactId;
  if (params.dealId) clean['dealId'] = params.dealId;
  return useQuery({
    queryKey: propertyKeys.calculatorContext(clean),
    queryFn: ({ signal }) => api<CalculatorContext>(`/calculator/context${query(clean)}`, { signal }),
    enabled: Boolean(clean['unitId'] ?? clean['dealId']),
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
    staleTime: 30_000,
  });
}

export function useUnitCalculations(unitId: string, enabled = true) {
  return useQuery({
    queryKey: propertyKeys.unitCalculations(unitId),
    queryFn: ({ signal }) => api<{ items: Calculation[] }>(`/units/${unitId}/calculations`, { signal }),
    enabled: enabled && unitId !== '',
    staleTime: 15_000,
  });
}

export function createInvestmentCalculation(body: CreateInvestmentCalculationBody): Promise<Calculation> {
  return api<Calculation>('/calculations', { method: 'POST', body });
}

/**
 * „PDF speichern": saves the calculation's PDF (`GET /files/:pdfFileId`, the session cookie of the
 * workspace) under the server's file name; resolves with that name.
 */
export function downloadCalculationPdf(calculation: Calculation): Promise<string> {
  if (calculation.pdfFileId === null) {
    return Promise.reject(
      new ApiError(409, { code: 'CONFLICT', message: 'Für diese Berechnung liegt kein PDF vor.' }),
    );
  }
  const fallback = `Immobilienrechner-${(calculation.unitNumber ?? 'Einheit').replaceAll(/[^A-Za-z0-9.]+/g, '_')}-V${calculation.version}.pdf`;
  return downloadFile(`/files/${calculation.pdfFileId}`, fallback);
}

export function useContactSearch(q: string, enabled: boolean) {
  return useQuery({
    queryKey: propertyKeys.contactSearch(q),
    queryFn: ({ signal }) =>
      api<{ items: ContactSearchItem[] }>(`/contacts${query({ q, limit: '8' })}`, { signal }),
    enabled: enabled && q.trim().length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

interface ContactRecordLinked {
  linked: { key: string; items: ContactLinkedDeal[] }[];
}

/** The deals of a contact for „Im Deal speichern": the record's linked group „Deals". */
export function useContactDeals(contactId: string | null) {
  return useQuery({
    queryKey: propertyKeys.contactDeals(contactId ?? ''),
    queryFn: async ({ signal }) => {
      const record = await api<ContactRecordLinked>(`/contacts/${contactId ?? ''}`, { signal });
      return record.linked.find((group) => group.key === 'deals')?.items ?? [];
    },
    enabled: contactId !== null && contactId !== '',
    staleTime: 15_000,
  });
}

// --- public developer upload page ---------------------------------------------------------------------

export function usePublicUploadPage(token: string): UseQueryResult<PublicUploadPage, Error> {
  return useQuery({
    queryKey: propertyKeys.publicPage(token),
    queryFn: ({ signal }) => api<PublicUploadPage>(`/public/upload/${encodeURIComponent(token)}`, { signal }),
    enabled: token !== '',
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
    staleTime: 0,
  });
}

export function uploadPublicRequest(
  token: string,
  requestId: string,
  file: File,
  options: UploadOptions = {},
): Promise<PublicUploadResponse> {
  const form = new FormData();
  form.set('file', file, file.name);
  return uploadWithProgress<PublicUploadResponse>(
    `/public/upload/${encodeURIComponent(token)}/requests/${requestId}`,
    form,
    options,
  );
}

// --- what the unit record reads from the reservations and deals modules ----------------------------

export function useReservationItem(reservationId: string | null) {
  return useQuery({
    queryKey: [...queryRoots.reservations, 'item', reservationId ?? ''] as const,
    queryFn: ({ signal }) => api<ReservationItemLite>(`/reservations/${reservationId ?? ''}`, { signal }),
    enabled: reservationId !== null && reservationId !== '',
    staleTime: 15_000,
  });
}

export function useDealAppointments(dealId: string | null) {
  return useQuery({
    queryKey: [...queryRoots.deal, dealId ?? '', 'appointments'] as const,
    queryFn: ({ signal }) =>
      api<{ items: DealAppointmentItem[] }>(`/deals/${dealId ?? ''}/appointments`, { signal }),
    enabled: dealId !== null && dealId !== '',
    retry: false,
    staleTime: 30_000,
  });
}
